/**
 * API client.
 *
 * One place that knows how to talk to the server: token handling, refresh, idempotency
 * keys and error shaping. Components never call fetch directly.
 */
import { idempotencyKey, type ApiError } from '@iecn/shared';

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

const ACCESS_KEY = 'iecn.access';
const REFRESH_KEY = 'iecn.refresh';

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(status: number, payload: ApiError['error']) {
    super(payload.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = payload.code;
    this.details = payload.details;
    this.requestId = payload.requestId;
  }

  /** Field-level messages for form display. */
  fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    const out: Record<string, string> = {};
    for (const issue of this.details as Array<{ field?: string; message?: string }>) {
      if (issue.field && issue.message) out[issue.field] = issue.message;
    }
    return out;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Send an Idempotency-Key. Required for anything that creates money or custody. */
  idempotent?: boolean;
  auth?: boolean;
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return false;

  // Collapse concurrent 401s into a single refresh.
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        clearTokens();
        return false;
      }
      const data = (await response.json()) as { accessToken: string; refreshToken: string };
      setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, idempotent, auth = true, headers, ...rest } = options;

  const send = async (): Promise<Response> => {
    const requestHeaders = new Headers(headers);
    if (body !== undefined) requestHeaders.set('Content-Type', 'application/json');
    if (idempotent) requestHeaders.set('Idempotency-Key', idempotencyKey());
    const token = getAccessToken();
    if (auth && token) requestHeaders.set('Authorization', `Bearer ${token}`);

    return fetch(`${BASE}${path}`, {
      ...rest,
      headers: requestHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  };

  let response = await send();

  if (response.status === 401 && auth && getAccessToken()) {
    const refreshed = await refreshTokens();
    if (refreshed) response = await send();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const errorPayload = (payload as ApiError | null)?.error ?? {
      code: 'INTERNAL_ERROR',
      message: 'Something went wrong. Please try again.',
    };
    throw new ApiRequestError(response.status, errorPayload);
  }

  return payload as T;
}

/**
 * Subscribe to a server-sent event stream.
 * Returns a disposer. Reconnection is handled by the browser's EventSource.
 */
export function subscribe(
  path: string,
  handlers: Record<string, (data: unknown) => void>,
  onError?: () => void,
): () => void {
  const token = getAccessToken();
  // EventSource cannot set headers, so authenticated streams carry the token as a query
  // parameter. These endpoints are read-only and the token is short-lived.
  const url = `${BASE}${path}${token ? `${path.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}` : ''}`;
  const source = new EventSource(url);

  for (const [event, handler] of Object.entries(handlers)) {
    source.addEventListener(event, (message) => {
      try {
        handler(JSON.parse((message as MessageEvent).data));
      } catch {
        // A malformed frame should never take down the stream.
      }
    });
  }

  source.onerror = () => onError?.();
  return () => source.close();
}
